import ActionButton from 'components/ActionButton';
import React, { useState } from 'react';
import styles from './index.module.scss';
import * as yup from 'yup';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers';
import { Error } from 'components/message/Error';
import { showError, showSuccess, stringifyAndParseObj } from 'utils';
import CategoriesDropwdown from 'components/CategoriesDropwdown';
import InventoryItemUnitDropwdown from 'components/InventoryItemUnitDropdown';
import { createInventoryItem } from 'services';

const validationSchema = yup.object().shape({
  name: yup.string().required(),
  category: yup.string().required('Please select a category'),
  price: yup.string().required(),
});

type FormData = {
  name: string;
  category: string;
  description: string;
  price: string;
};

const CreateInventoryItem = props => {
  const { onCancel, onCreate, supplierId, populatedItemData = {}} = props;
  const { name = '', price = 0 } = populatedItemData;
  const [loading, setLoading] = useState<boolean>(false);
  const [unit, setUnit] = useState<string>('');
  const [supplierUnit, setSupplierUnit] = useState<string>('');
  const [error, setError] = useState({}) as any;

  const {
    register,
    handleSubmit,
    errors,
    getValues,
  } = useForm<FormData>({ resolver: yupResolver(validationSchema) });

  const createInventoryItemFn = handleSubmit(async ({ name, category, description, price }) => {
    setError({});
    setLoading(true);
    const data = {
      name,
      categoryId: category,
      description,
      unitId: unit,
      supplierUnitId: supplierUnit,
      price: parseFloat(price),
      supplierId,
    };

    try {
      let response = await createInventoryItem(data);
      response = stringifyAndParseObj(response);
      showSuccess('Inventory Item created successfully.');
      if (onCreate) {
        onCreate(response);
      } else {
        props.history.replace(`/inventory-items/${response.objectId}`);
      }
    } catch (e) {
      showError(e);
    } finally {
      setLoading(false);
    }
  });

  const handleClick = () => {
    let errorsTemp = {};
    if (!unit.length) {
      errorsTemp = { ...errorsTemp, unit: 'unit' };
    }
    if (!supplierUnit.length && supplierId) {
      errorsTemp = { ...errorsTemp, supplierUnit: 'supplierUnit' };
    }
    setError(errorsTemp);
    if (Object.keys(errorsTemp)?.length) {
      return;
    }
    createInventoryItemFn();
  };

  const onClickCancelButton = () => {
    if (onCancel) {
      onCancel();
    } else {
      props.history.goBack();
    }
  };

  const setUnitFn = (objectId) => {
    const errorsTemp = { ...error } as any;
    if (objectId) {
      delete errorsTemp.unit;
      setError(errorsTemp);
      setUnit(objectId);
    }
  };

  const setSupplierUnitFn = (objectId) => {
    const errorsTemp = { ...error } as any;
    if (objectId) {
      delete errorsTemp.supplierUnit;
      console.log(errorsTemp, '2');

      setError(errorsTemp);
      setSupplierUnit(objectId);
    }
  };

  const getSupplierUnitInput = () => {
    if (!supplierId) return null;
    return (
      <div className={styles['unit']}>
        <div className={styles['label']}>Supplier Unit</div>
        <InventoryItemUnitDropwdown
          loading={loading}
          value={supplierUnit}
          onSelect={setSupplierUnitFn}
        />
        {error?.supplierUnit && <Error message={'Please select a supplierr unit'} />}
      </div>
    );
  };

  return (
    <div className={styles['main-container']}>
      <div className={styles['header']}>
        <div className={styles['heading']}>Create a New Inventory Item</div>
      </div>
      <div className={styles['item-details']}>
        <div className={styles['name']}>
          <div className={styles['label']}>Name</div>
          <input
            name={'name'}
            defaultValue={name}
            ref={register}
            className={'form-control'}
            disabled={loading}
          />
          {errors.name && <Error message={errors.name.message} />}
        </div>
        <div className={styles['price']}>
          <div className={styles['label']}>Price</div>
          <input
            name={'price'}
            type={'number'}
            defaultValue={parseFloat(price)}
            ref={register}
            className={'form-control'}
            disabled={loading}
          />
          {errors.price && <Error message={errors.price.message} />}
        </div>
        <div className={styles['category']}>
          <div className={styles['label']}>Category</div>
          <CategoriesDropwdown
            disabled={loading}
            componentRef={register}
            value={getValues('category')}
          />
          {errors.category && <Error message={errors.category.message} />}
        </div>
        <div className={styles['unit']}>
          <div className={styles['label']}>Unit</div>
          <InventoryItemUnitDropwdown
            loading={loading}
            value={unit}
            onSelect={setUnitFn}
          />
          {error?.unit && <Error message={'Please select a unit'} />}
        </div>
        {getSupplierUnitInput()}
        <div className={styles['description']}>
          <div className={styles['label']}>Description</div>
          <textarea
            name={'description'}
            ref={register}
            className={'form-control'}
            disabled={loading}
          />
        </div>
      </div>
      <div className={styles['footer']}>
        <ActionButton
          text={'Cancel'}
          active={!loading}
          isLoading={false}
          onClick={onClickCancelButton}
          customClass={styles['cancel']}
        />
        <ActionButton
          text={'Create Item'}
          active={!loading}
          isLoading={loading}
          onClick={handleClick}
        />
      </div>
    </div>
  );
};

export default CreateInventoryItem;
